import { type Permission } from '@ctcells/core'
import { t, type MessageKey } from '@ctcells/i18n'
import { useNavigate } from 'react-router'

import type { TabItem } from '../layout/Tabs.tsx'

import type { ScreenAccess } from './access.ts'

/**
 * **사이드바 항목 하나가 여러 화면을 탭으로 품는 구조**의 공용 기계.
 *
 * 2026-09-18 eric 지시로 두 번 생겼다 — 모듈·로그가 「관리」로, 시료·레시피·랩웨어가 「준비」로
 * (그 문 이름은 2026-09-21 「자원」에서 「준비」로, 2026-10-01 시안을 따라 「실험 준비」로 바뀌었다 — 키는
 * `nav.item.resources` 그대로다. 무리 제목 h2 는 `screen.resourcesGroup.title` 「준비」로 따로 선다).
 * 🔴 **두 벌로 복붙하지 마라.** 같은 문제라 여기 한 곳에서 푼다. 무리마다 다른 것은
 *    **탭 목록(데이터)뿐**이고, 잠금·이동·라벨 규칙은 전부 공유한다.
 *
 * 🔴 **탭이 곧 주소다.** `useState` 로 들면 ⓐ 새로고침에 탭이 초기화되고 ⓑ 상세 화면에서
 *    「←」로 돌아올 때 엉뚱한 탭이 열린다(사용자 탭에서 실제로 겪었다). 주소와 묶으면 둘 다 안 생긴다.
 *
 * 🔴 **권한 없는 탭은 숨기지 않고 비활성 + 사유**다(CLAUDE.md RBAC · 사이드바 자물쇠와 같은 규칙).
 * ⚠ 여기 값은 **노출 판단 전용**이다. 집행은 서버 403 `INSUFFICIENT_ROLE` 이고,
 *   프론트에서 막았다는 것이 안전을 보장하지 않는다.
 */
export interface GroupTabDef<Id extends string = string> {
  id: Id
  /** 이 탭의 주소. 사이드바에서 그 무리로 들어오는 문이기도 하다 */
  path: string
  /** 화면 표기는 사전이 쥔다 — 여기 한글을 넣지 않는다 */
  labelKey: MessageKey
  /**
   * 이 탭에 들어가는 데 필요한 권한.
   * ⚠ 한 무리 안에서 값이 같아도 **자리를 비우지 않는다** — 나중에 갈릴 때 구조가 흔들리지 않게,
   *   그리고 두 무리가 서로 다른 모양이 되지 않게 한다.
   */
  permission: Permission
}

export interface GroupTabsBinding<Id extends string> {
  tabs: TabItem<Id>[]
  value: Id
  onChange: (id: Id) => void
}

export function useGroupTabs<Id extends string>(
  defs: readonly GroupTabDef<Id>[],
  active: Id,
  access: ScreenAccess,
): GroupTabsBinding<Id> {
  const navigate = useNavigate()
  const held = access.permissions

  const tabs = defs.map((tab) => {
    // ⚠ `undefined` 는 **아직 모르는 것**이라 잠그지 않는다 — 프론트가 역할에서 권한을 유도하지 않는다
    const locked = held !== undefined && !held.includes(tab.permission)
    return {
      id: tab.id,
      label: t(tab.labelKey),
      ...(locked
        ? { disabled: true, disabledReason: t('reason.noPermission', { perm: tab.permission }) }
        : {}),
    }
  })

  return {
    tabs,
    value: active,
    onChange: (id) => {
      const next = defs.find((tab) => tab.id === id)
      if (next) void navigate(next.path)
    },
  }
}

/**
 * 🔴 **무리 탭을 그릴지 말지 정하는 «단 하나의» 규칙.** 세 무리(ops · resource · admin)와
 *    두 콘솔 관점이 전부 이것을 본다 — 화면마다 따로 판단하면 반드시 어긋난다.
 *
 * > **열린 탭(`active`)을 안 넘기면 무리 탭 줄을 그리지 않는다.**
 *
 * 넘기는 주체는 **라우트**다(탭이 곧 주소다). 안 넘기는 표면이 실제로 셋 있다:
 *
 * | 표면 | 왜 안 넘기나 |
 * | --- | --- |
 * | 태블릿 kiosk | 넘기면 kiosk 사용자가 콘솔 경로로 튕겨 나간다 |
 * | 모듈 패드 | 무리라는 개념 자체가 없다 |
 * | **장비 관점 콘솔** | 화면 10개가 **사이드바에 각자 서 있다** — 탭 줄이 또 뜨면 같은 곳으로 가는 길이 두 개가 된다 |
 *
 * ⚠ **관리 화면은 이 규칙을 쓰지 않는다.** 거기가 그 무리의 «본거지»라 줄이 없으면 사용자 ↔ 일반을
 *   오갈 길이 사라진다 — 끄는 대신 목록을 갈아 끼운다(`AdminScreenProps.adminTabs`).
 *
 * 🔴 훅은 **언제나 부른다.** 조건부 호출은 React 규칙 위반이라 「부르고 버리는」 모양이 맞다.
 *
 * @param fallback 줄을 안 그릴 때도 `useGroupTabs` 에 넣어야 하는 자리값. 화면 밖으로 안 나간다.
 * @returns 무리 탭 묶음, 또는 **그리지 않음(`null`)**
 */
export function useGroupTabLayout<Id extends string>(
  defs: readonly GroupTabDef<Id>[],
  active: Id | undefined,
  fallback: Id,
  access: ScreenAccess,
): GroupTabsBinding<Id> | null {
  const bound = useGroupTabs(defs, active ?? fallback, access)
  return active === undefined ? null : bound
}
